import clsx from 'clsx';
import moment from 'moment';
import React from 'react';

import { DEFAULT_EMPTY_VALUE } from '../../../Config';
import FormWrapper from '../../../Elements/FormWrapper/FormWrapper';
import {
  CheckPaymentTreatment,
  FormTypeOption,
  PaidBillTreatment,
  Treatment,
  UnpaidBillTreatment,
} from '../../../types/treatment';
import FieldWrapper from '../Fields/FieldWrapper/FieldWrapper';
import FormGroupContainer from '../Form/FormGroupContainer/FormGroupContainer';

const formatDate = (date: string) => {
  return date ? moment(date).format('DD-MMM YYYY') : DEFAULT_EMPTY_VALUE;
};

const FormsDisplayLayout = ({
  treatment,
  disabledFields,
}: {
  treatment: Treatment;
  disabledFields: FormTypeOption['disabledFields'];
}) => {
  const { typeDisplayName, payload } = treatment;
  const {
    isMethodDisabled: isMethodHidden,
    isAccountDisabled: isAccountHidden,
    isTermsDisabled: isTermsHidden,
    isDueOnDisabled: isDueOnHidden,
  } = disabledFields;

  return (
    <FormWrapper>
      <FormGroupContainer
        className={clsx(
          'tw-w-full tw-flex',
          'tw-mb-[15px]',
          'tw-border-b-[1px] tw-border-solid tw-border-cfo-grey-500'
        )}
      >
        <FormGroupContainer className={clsx('tw-max-w-[180px] tw-w-full', 'tw-mr-[10px]')}>
          <FieldWrapper
            label={'Type'}
            labelClassName={clsx('tw-text-cfo-black')}
            labelWidthClasses={clsx('tw-min-w-[35px] tw-max-w-[35px]')}
          >
            <span className={clsx('tw-text-cfo-black')}>
              {typeDisplayName || DEFAULT_EMPTY_VALUE}
            </span>
          </FieldWrapper>
        </FormGroupContainer>

        <FormGroupContainer className={clsx('tw-w-full')}>
          {!isAccountHidden && (
            <FieldWrapper
              label={'Payment Account'}
              labelClassName={clsx('tw-text-cfo-black')}
              labelWidthClasses={clsx('tw-min-w-[112px] tw-max-w-[112px]')}
            >
              <span className={clsx('tw-text-cfo-black')}>
                {(payload as PaidBillTreatment['payload']).paymentAccount?.name ||
                  (payload as CheckPaymentTreatment['payload']).bankAccount?.name ||
                  DEFAULT_EMPTY_VALUE}
              </span>
            </FieldWrapper>
          )}
          {!isMethodHidden && (
            <FieldWrapper
              label={'Payment Method'}
              labelClassName={clsx('tw-text-cfo-black')}
              labelWidthClasses={clsx('tw-min-w-[112px] tw-max-w-[112px]')}
            >
              <span className={clsx('tw-text-cfo-black')}>
                {(payload as PaidBillTreatment['payload']).paymentMethod?.name ||
                  DEFAULT_EMPTY_VALUE}
              </span>
            </FieldWrapper>
          )}
        </FormGroupContainer>
      </FormGroupContainer>

      <FormGroupContainer className={clsx('tw-w-full')}>
        <FieldWrapper
          label={'Supplier/Payee'}
          labelClassName={clsx('tw-text-cfo-black')}
          labelWidthClasses={clsx('tw-min-w-[98px] tw-max-w-[98px]')}
        >
          <span className={clsx('tw-text-cfo-black')}>
            {(payload as UnpaidBillTreatment['payload'])?.vendor?.name ||
              (payload as PaidBillTreatment['payload'])?.payee?.name ||
              DEFAULT_EMPTY_VALUE}
          </span>
        </FieldWrapper>
      </FormGroupContainer>

      <FormGroupContainer className={clsx('tw-w-full tw-flex')}>
        <FieldWrapper
          label={'Document ref'}
          labelClassName={clsx('tw-text-cfo-black')}
          labelWidthClasses={clsx('tw-min-w-[98px] tw-max-w-[98px]')}
          containerClassName={clsx('tw-min-w-[300px] tw-w-full tw-mr-[10px]')}
        >
          <span className={clsx('tw-text-cfo-black')}>
            {payload.referenceNumber || DEFAULT_EMPTY_VALUE}
          </span>
        </FieldWrapper>
        <FieldWrapper
          label={'Date'}
          labelClassName={clsx('tw-text-cfo-black')}
          labelWidthClasses={clsx('tw-min-w-[50px] tw-max-w-[50px]')}
        >
          <span className={clsx('tw-text-cfo-black')}>{formatDate(payload.transactionDate)}</span>
        </FieldWrapper>
      </FormGroupContainer>

      <FormGroupContainer className={clsx('tw-w-full tw-flex')}>
        {!isTermsHidden && (
          <FieldWrapper
            label={'Terms'}
            labelClassName={clsx('tw-text-cfo-black')}
            labelWidthClasses={clsx('tw-min-w-[98px] tw-max-w-[98px]')}
            containerClassName={clsx('tw-min-w-[300px] tw-w-full tw-mr-[10px]')}
          >
            <span className={clsx('tw-text-cfo-black')}>
              {(payload as UnpaidBillTreatment['payload']).salesTerm?.name || DEFAULT_EMPTY_VALUE}
            </span>
          </FieldWrapper>
        )}
        {!isDueOnHidden && (
          <FieldWrapper
            label={'Due on'}
            labelClassName={clsx('tw-text-cfo-black')}
            labelWidthClasses={clsx('tw-min-w-[50px] tw-max-w-[50px]')}
          >
            <span className={clsx('tw-text-cfo-black')}>
              {formatDate((payload as UnpaidBillTreatment['payload']).dueDate)}
            </span>
          </FieldWrapper>
        )}
      </FormGroupContainer>

      <FormGroupContainer className={clsx('tw-max-w-[300px] tw-w-full tw-flex')}>
        <FieldWrapper
          label={'Location'}
          labelClassName={clsx('tw-text-cfo-black')}
          labelWidthClasses={clsx('tw-min-w-[98px] tw-max-w-[98px]')}
        >
          <span className={clsx('tw-text-cfo-black')}>
            {payload.location?.name || DEFAULT_EMPTY_VALUE}
          </span>
        </FieldWrapper>
      </FormGroupContainer>
    </FormWrapper>
  );
};

export default FormsDisplayLayout;
